import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/services/api";
import { APPSC_PAPERS } from "./APPSCMainsHubPage";
import {
  ArrowLeft, PenTool, Send, Loader2, CheckCircle, Star,
  AlertTriangle, TrendingUp, BookOpen, Target, Award, Zap,
  BarChart3
} from "lucide-react";

const APPSCAnswerWritingPage = () => {
  const { paperId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();

  const paper = APPSC_PAPERS.find(p => p.id === paperId);

  const [question, setQuestion] = useState("");
  const [marks, setMarks] = useState(15);
  const [wordLimit, setWordLimit] = useState(250);
  const [userAnswer, setUserAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [phase, setPhase] = useState("write"); // write, result

  if (!paper) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500">Paper not found</p>
          <Button onClick={() => navigate("/appsc/mains")} className="mt-4">Back to Mains Hub</Button>
        </div>
      </div>
    );
  }

  const handleSubmit = async () => {
    if (!question.trim() || question.trim().length < 10) {
      toast({ variant: "destructive", description: "Enter a question (min 10 characters)" });
      return;
    }
    if (!userAnswer.trim() || userAnswer.trim().length < 30) {
      toast({ variant: "destructive", description: "Write your answer (min 30 characters)" });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/aspirant-network/appsc/evaluate-answer`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          question: question.trim(),
          answer: userAnswer.trim(),
          paper_id: paper.id,
          paper_name: paper.name,
          marks,
          word_limit: wordLimit,
        }),
      });
      if (!res.ok) throw new Error("Evaluation failed");
      const data = await res.json();
      setEvaluation(data);
      setPhase("result");
    } catch {
      toast({ variant: "destructive", description: "Failed to evaluate. Try again." });
    } finally {
      setLoading(false);
    }
  };

  const wordCount = userAnswer.trim() ? userAnswer.trim().split(/\s+/).length : 0;

  // ─── WRITE PHASE ───────────────────────────────────────────────
  if (phase === "write") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30">
        <div className={`bg-gradient-to-r ${paper.color} text-white`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate("/appsc/mains")} className="p-2 bg-white/10 hover:bg-white/20 rounded-xl" data-testid="paper-back-btn">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold">{paper.name}</h1>
                <p className="text-white/70 text-sm">Answer Writing Practice</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">
          {/* Topics Reference */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Topics covered in this paper</p>
              <div className="flex flex-wrap gap-1.5">
                {paper.topics.map((t) => (
                  <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Question Input */}
          <Card>
            <CardContent className="p-5 space-y-4">
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-2">Your Question</label>
                <Textarea
                  rows={3}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder={`Enter a ${paper.shortName} question for answer practice...`}
                  className="resize-none border-2 focus:border-blue-400"
                  data-testid="appsc-question-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-slate-700 block mb-2">Marks</label>
                  <div className="flex gap-2">
                    {[10, 15, 20, 25].map((m) => (
                      <button key={m} onClick={() => { setMarks(m); setWordLimit(m * 15); }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium ${marks === m ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                        data-testid={`marks-${m}`}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700 block mb-2">Word Limit</label>
                  <input
                    type="number" value={wordLimit} onChange={(e) => setWordLimit(parseInt(e.target.value) || 150)}
                    className="w-full px-3 py-2 text-sm border-2 border-slate-200 rounded-lg focus:border-blue-400 focus:outline-none"
                    min="50" max="500"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Answer Writing Area */}
          <Card>
            <CardContent className="p-0">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <PenTool className="w-4 h-4 text-blue-500" /> Write Your Answer
                </span>
                <span className="text-sm text-slate-500">
                  {wordCount} words <span className="text-slate-400">/ {wordLimit} target</span>
                </span>
              </div>
              <textarea
                value={userAnswer}
                onChange={(e) => setUserAnswer(e.target.value)}
                placeholder="Start writing your answer here. Focus on structure: Introduction, Body (with subheadings), Conclusion..."
                className="w-full min-h-[350px] p-5 text-base leading-relaxed resize-none focus:outline-none"
                data-testid="appsc-answer-input"
              />
            </CardContent>
          </Card>

          <Button
            onClick={handleSubmit}
            disabled={loading || !question.trim() || !userAnswer.trim()}
            className={`w-full py-3 bg-gradient-to-r ${paper.color} hover:opacity-90 text-white font-semibold`}
            data-testid="appsc-submit-btn"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Evaluating with AI...</>
            ) : (
              <><Send className="w-4 h-4 mr-2" /> Submit & Get AI Evaluation</>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // ─── RESULT PHASE ──────────────────────────────────────────────
  if (phase === "result" && evaluation) {
    const scorePercent = evaluation.max_marks > 0 ? Math.round((evaluation.score / evaluation.max_marks) * 100) : 0;
    const scoreColor = scorePercent >= 70 ? "text-emerald-600" : scorePercent >= 50 ? "text-amber-600" : "text-red-600";

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30">
        <div className={`bg-gradient-to-r ${paper.color} text-white`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
            <div className="flex items-center gap-3">
              <button onClick={() => { setPhase("write"); setEvaluation(null); }} className="p-2 bg-white/10 hover:bg-white/20 rounded-xl">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-bold">{paper.shortName} — Evaluation</h1>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">
          {/* Score Card */}
          <Card className="border-2 border-blue-200">
            <CardContent className="p-6 text-center">
              <p className={`text-5xl font-bold ${scoreColor}`}>{evaluation.score}/{evaluation.max_marks}</p>
              <p className="text-sm text-slate-500 mt-1">{evaluation.word_count} words written</p>
              {evaluation.grade && (
                <Badge className={`mt-2 text-sm ${evaluation.grade === 'A' ? 'bg-emerald-100 text-emerald-700' : evaluation.grade === 'B' ? 'bg-blue-100 text-blue-700' : evaluation.grade === 'C' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                  Grade {evaluation.grade}
                </Badge>
              )}

              {/* Sub-scores */}
              {evaluation.rubric && (
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-5">
                  {Object.entries(evaluation.rubric).map(([key, val]) => (
                    <div key={key} className="bg-slate-50 rounded-xl p-2">
                      <p className="text-lg font-bold text-slate-800">{val.score}</p>
                      <p className="text-[10px] text-slate-500 capitalize">{key.replace(/_/g, ' ')}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Overall Feedback */}
          {evaluation.overall_feedback && (
            <Card>
              <CardContent className="p-5">
                <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-blue-500" /> Overall Feedback</h3>
                <p className="text-sm text-slate-700 leading-relaxed">{evaluation.overall_feedback}</p>
              </CardContent>
            </Card>
          )}

          {/* Strengths & Improvements */}
          <div className="grid md:grid-cols-2 gap-4">
            {evaluation.strengths?.length > 0 && (
              <Card className="border-emerald-200">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-emerald-700 mb-3 flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Strengths</h3>
                  <ul className="space-y-2">
                    {evaluation.strengths.map((s, i) => (
                      <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                        <Star className="w-3 h-3 text-emerald-500 mt-1 shrink-0" /> {s}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
            {evaluation.improvements?.length > 0 && (
              <Card className="border-amber-200">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-amber-700 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Areas to Improve</h3>
                  <ul className="space-y-2">
                    {evaluation.improvements.map((s, i) => (
                      <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                        <TrendingUp className="w-3 h-3 text-amber-500 mt-1 shrink-0" /> {s}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Model Answer Suggestion */}
          {evaluation.model_structure && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-5">
                <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2"><BookOpen className="w-4 h-4" /> Suggested Answer Structure</h3>
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{evaluation.model_structure}</p>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <Button className={`flex-1 bg-gradient-to-r ${paper.color} hover:opacity-90 text-white`}
              onClick={() => { setPhase("write"); setEvaluation(null); setUserAnswer(""); setQuestion(""); }}
              data-testid="appsc-write-another">
              <PenTool className="w-4 h-4 mr-2" /> Write Another
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => navigate("/appsc/mains")}>
              Back to Mains Hub
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default APPSCAnswerWritingPage;
